import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import {
  Box,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogTitle,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";

import {
  getEmpAdmins,
  selectEmpAdmin,
} from "../../empAdmin/store/empAdminSlice";
import {
  getLocalAdmin,
  selectLocalById,
  updateLocalAdmin,
  removeLocalAdmin,
} from "../store/localAdminSlice";
import { getUsers, selectUser } from "../../userAdmin/store/userSlice";
import { useDeepCompareEffect } from "@fuse/hooks";

function LocalEditForm({ handleClose, localId }) {
  const dispatch = useDispatch();
  const local = useSelector((state) => selectLocalById(state, localId));
  const empAdmins = useSelector(selectEmpAdmin);
  const users = useSelector(selectUser);

  const [openDelete, setOpenDelete] = useState(false);
  const [premiumChecked, setPremiumChecked] = useState(local?.premium || false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const schema = yup.object().shape({
    fantasy_name: yup.string().trim().required("Nombre de fantasía requerido"),
    address: yup.string().required("Sucursal requerida"),
    company_name: yup.string().trim().required("Nombre de compañía requerido"),
    rut: yup.string().trim().required("Rut requerido"),
    id_emp_admin: yup.string().optional(),
    num_auth_sani: yup.string().nullable().optional(),
    client_id: yup.string().nullable().optional(),
    auditor_id: yup.string().nullable().optional(),
    supervisor_id: yup.string().nullable().optional(),
  });

  const { control, handleSubmit, reset, formState } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      fantasy_name: local?.fantasy_name || "",
      address: local?.address || "",
      company_name: local?.company_name || "",
      rut: local?.rut || "",
      id_emp_admin: local?.emp_admin?.id || "",
      num_auth_sani: local?.num_auth_sani || "",
      client_id: local?.users?.find(u => u.rol_id === 4)?.id || "",
      auditor_id: local?.users?.find(u => u.rol_id === 3)?.id || "",
      supervisor_id: local?.users?.find(u => u.rol_id === 2)?.id || "",
    },
  });

  useDeepCompareEffect(() => {
    dispatch(getEmpAdmins());
    dispatch(getLocalAdmin(localId));
    dispatch(getUsers());
  }, [dispatch]);

  useEffect(() => {
    if (local) reset(local);
  }, [local, reset]);

  const notify = (msg, type = "success") =>
    toast[type](msg, { position: "top-center", autoClose: 1500 });

  const onSubmit = (data) => {
    data.id = local.id;
    data.premium = premiumChecked;
    dispatch(updateLocalAdmin(data)).then(() => {
      notify("Local actualizado correctamente");
      handleClose();
    });
  };

  const handleRemoveLocal = () => {
    dispatch(removeLocalAdmin(local)).then(() => {
      notify("Local eliminado", "error");
      handleClose();
    });
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
        gap: 2,
        p: 3,
        maxHeight: "80vh",
        overflowY: "auto",
      }}
    >
      <Box sx={{ gridColumn: "1 / -1", textAlign: "center", mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          {local?.fantasy_name || "Editar Local"}
        </Typography>
        <Divider sx={{ mt: 1 }} />
      </Box>

      {/* Nombre de fantasía */}
      <Controller
        control={control}
        name="fantasy_name"
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="Nombre de fantasía"
            placeholder="Nombre de fantasía"
            fullWidth
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FuseSvgIcon size={20}>heroicons-solid:user-circle</FuseSvgIcon>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      {/* Sucursal */}
      <Controller
        control={control}
        name="address"
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="Sucursal"
            placeholder="Sucursal"
            fullWidth
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FuseSvgIcon size={20}>heroicons-solid:mail</FuseSvgIcon>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      {/* Compañía */}
      <Controller
        control={control}
        name="company_name"
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="Nombre Compañía"
            fullWidth
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />

      {/* Rut */}
      <Controller
        control={control}
        name="rut"
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="Rut"
            fullWidth
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />

      {/* Cliente */}
      <Controller
        control={control}
        name="client_id"
        render={({ field }) => (
          <FormControl fullWidth>
            <InputLabel>Cliente Administrador</InputLabel>
            <Select {...field} label="Cliente Administrador">
              {users
                .filter((u) => u.rol_id === 4)
                .map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}
      />

      {/* Supervisor */}
      <Controller
        control={control}
        name="supervisor_id"
        render={({ field }) => (
          <FormControl fullWidth>
            <InputLabel>Supervisor</InputLabel>
            <Select {...field} label="Supervisor">
              {users
                .filter((u) => u.rol_id === 2)
                .map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}
      />

      {/* Auditor */}
      {/* <Controller
        control={control}
        name="auditor_id"
        render={({ field }) => (
          <FormControl fullWidth>
            <InputLabel>Auditor</InputLabel>
            <Select {...field} label="Auditor">
              {users
                .filter((u) => u.rol_id === 3)
                .map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}
      /> */}

      {/* Empresa Administradora */}
      <Controller
        control={control}
        name="id_emp_admin"
        render={({ field }) => (
          <FormControl fullWidth>
            <InputLabel>Empresa Administradora</InputLabel>
            <Select {...field} label="Empresa Administradora">
              {empAdmins.map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {e.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      {/* Número autorización sanitaria */}
      <Controller
        control={control}
        name="num_auth_sani"
        render={({ field }) => (
          <TextField
            {...field}
            label="N° Autorización Sanitaria"
            fullWidth
          />
        )}
      />

      {/* Premium */}
      <FormControlLabel
        control={
          <Switch
            checked={premiumChecked}
            onChange={() => setPremiumChecked(!premiumChecked)}
          />
        }
        label="Premium"
        sx={{ gridColumn: "1 / -1" }}
      />

      {/* Botones */}
      <Box
        sx={{
          gridColumn: "1 / -1",
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mt: 2,
        }}
      >
        <Button variant="outlined" onClick={handleClose}>
          Cancelar
        </Button>
        <Button variant="contained" color="secondary" onClick={handleSubmit(onSubmit)}>
          Guardar
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setOpenDelete(true)}
        >
          Eliminar
        </Button>
      </Box>

      {/* Dialogo de eliminación */}
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>¿Deseas eliminar este local?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancelar</Button>
          <Button onClick={handleRemoveLocal} autoFocus color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LocalEditForm;
